/**
 * DefaultsInspoAdminPage - Manage platform-wide defaults and inspiration content
 * Features: Field options (dietary, accessibility, etc.), inspiration content, platform config
 */
import { useState, useEffect } from "react";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Settings,
  Sparkles,
  Plus,
  RefreshCw,
  Trash2,
  Edit2,
  Check,
  X,
  Loader2,
  RotateCcw,
  Heart,
  Users,
  Utensils,
  Accessibility,
  Building,
  Target,
  Music,
  FileText,
  Image,
  Star,
  GripVertical,
  Shirt,
  Footprints,
  Gem,
  CircleDot,
  Scissors,
  Paintbrush,
  Palette,
  Eye,
  Calendar,
  Globe,
  Leaf,
  ShoppingBag,
  ChevronDown,
  Upload,
  ImagePlus
} from "lucide-react";

// Category icons - Main default categories only
// Note: Industries is a sub-field of Interests (not a main category)
// Note: Builder Types is collected in Intake/Personal Info (not a default)
const CATEGORY_CONFIG = {
  dietary_options: { icon: Utensils, label: "Dietary Options", color: "amber" },
  accessibility_options: { icon: Accessibility, label: "Accessibility", color: "blue" },
  audience_types: { icon: Users, label: "Audience Types", color: "violet" },
  interests: { 
    icon: Heart, 
    label: "Interests", 
    color: "rose",
    subFields: ["industries"]  // Industries is a sub-field
  },
  vibe_options: { icon: Music, label: "Vibe Options", color: "pink" },
  hair_options: { icon: Sparkles, label: "Hair Options", color: "fuchsia" },
  nails_options: { icon: Sparkles, label: "Nails Options", color: "rose" },
  face_options: { icon: Sparkles, label: "Face Options", color: "pink" },
  lashes_options: { icon: Sparkles, label: "Lashes Options", color: "purple" },
  travel_options: { icon: Sparkles, label: "Travel Options", color: "sky" },
  wellness_options: { icon: Heart, label: "Wellness Options", color: "emerald" },
  // Retail & Fashion - Style & Accessories (includes clothing, shoes, jewelry, metals)
  style_options: { icon: Shirt, label: "Style & Accessories", color: "violet", subCategories: ["clothing", "shoes", "jewelry", "metals"] }
  // NOTE: shoe_options, jewelry_options, metal_options are now merged into style_options
};

// Per-category config for platform settings
const CATEGORY_SETTINGS = [
  { key: "hair", label: "Hair", icon: Scissors, color: "#e83e8c", bgColor: "#ffeef4", 
    toggles: [
      { id: "collect_hair_profile", label: "Collect Hair Profile", desc: "Texture, length, state, protective styles" },
      { id: "collect_hair_color", label: "Collect Hair Color", desc: "Color preferences and history" },
      { id: "show_hair_inspo", label: "Show Hair Inspo", desc: "Hair inspiration gallery in /me" },
    ]},
  { key: "makeup", label: "Makeup", icon: Paintbrush, color: "#9c57f5", bgColor: "#f2e9ff",
    toggles: [
      { id: "collect_face_profile", label: "Collect Face Profile", desc: "Skin type, tone, coverage, finish" },
      { id: "collect_product_prefs", label: "Collect Product Prefs", desc: "Product links, brands used" },
      { id: "show_look_inspo", label: "Show Look Inspo", desc: "Look inspiration gallery in /me" },
    ]},
  { key: "nails", label: "Nails", icon: Palette, color: "#f15a70", bgColor: "#ffe8ec",
    toggles: [
      { id: "collect_nail_profile", label: "Collect Nail Profile", desc: "Shape, length, base, art styles" },
      { id: "show_nail_inspo", label: "Show Nail Inspo", desc: "Nail inspiration gallery in /me" },
    ]},
  { key: "lashes", label: "Lashes", icon: Eye, color: "#774cda", bgColor: "#ede7f7",
    toggles: [
      { id: "collect_lash_profile", label: "Collect Lash Profile", desc: "Type, style, length, curl" },
    ]},
  { key: "culinary", label: "Culinary", icon: Utensils, color: "#f28c28", bgColor: "#fff3e6",
    toggles: [
      { id: "collect_dietary", label: "Collect Dietary Info", desc: "Dietary restrictions and allergies" },
      { id: "collect_culinary_profile", label: "Collect Culinary Profile", desc: "Cuisine prefs, cooking level" },
    ]},
  { key: "events", label: "Events", icon: Calendar, color: "#f9c434", bgColor: "#fff5e6",
    toggles: [
      { id: "collect_dietary", label: "Collect Dietary Info", desc: "Dietary restrictions for events" },
      { id: "collect_accessibility", label: "Collect Accessibility", desc: "Accessibility needs" },
      { id: "collect_interests", label: "Collect Interests", desc: "Topic interests" },
      { id: "enable_waitlist", label: "Enable Waitlist", desc: "Waitlist for full events" },
    ]},
  { key: "wellness", label: "Wellness", icon: Heart, color: "#75d6d6", bgColor: "#e7f5f5",
    toggles: [
      { id: "collect_wellness_profile", label: "Collect Wellness Profile", desc: "Interests, fitness level, activities" },
    ]},
  { key: "travel", label: "Travel", icon: Globe, color: "#73b7ff", bgColor: "#e7f3ff",
    toggles: [
      { id: "collect_travel_profile", label: "Collect Travel Profile", desc: "Transport, lodging, room prefs" },
    ]},
  { key: "instructors", label: "Instructors", icon: Star, color: "#4a90d9", bgColor: "#e0eeff",
    toggles: [
      { id: "collect_audience_profile", label: "Collect Audience Profile", desc: "Learning styles, goals, experience" },
      { id: "enable_resources", label: "Enable Resources", desc: "Resource links for participants" },
    ]},
  { key: "style", label: "Retail", icon: ShoppingBag, color: "#9950fa", bgColor: "#f0e8ff",
    toggles: [
      { id: "collect_style_profile", label: "Collect Style Profile", desc: "Clothing, shoes, jewelry prefs" },
    ]},
  { key: "dispensary", label: "Dispensary", icon: Leaf, color: "#60d680", bgColor: "#e8fef0",
    toggles: [
      { id: "collect_wellness_profile", label: "Collect Wellness Profile", desc: "Wellness and product prefs" },
    ]},
];

function PerCategoryConfig({ platformConfig, onUpdate }) {
  const [expandedCat, setExpandedCat] = useState(null);
  
  return (
    <div className="space-y-2" data-testid="per-category-config">
      {CATEGORY_SETTINGS.map(cat => {
        const Icon = cat.icon;
        const isExpanded = expandedCat === cat.key;
        return (
          <div key={cat.key} className="border rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedCat(isExpanded ? null : cat.key)}
              className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
              data-testid={`config-cat-${cat.key}`}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: cat.bgColor }}>
                <Icon className="w-4 h-4" style={{ color: cat.color }} />
              </div>
              <span className="flex-1 font-medium text-sm">{cat.label}</span>
              <span className="text-xs text-gray-400">{cat.toggles.length} settings</span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
            {isExpanded && (
              <div className="px-3 pb-3 space-y-2 border-t bg-gray-50/50">
                {cat.toggles.map(toggle => (
                  <div key={toggle.id} className="flex items-center justify-between p-2.5 bg-white border rounded-lg">
                    <div>
                      <p className="text-sm font-medium">{toggle.label}</p>
                      <p className="text-xs text-gray-500">{toggle.desc}</p>
                    </div>
                    <Switch
                      checked={platformConfig[`${cat.key}_${toggle.id}`] ?? true}
                      onCheckedChange={(checked) => onUpdate({ [`${cat.key}_${toggle.id}`]: checked })}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Sub-field configurations (appear nested under their parent category)
const SUB_FIELD_CONFIG = {
  industries: { icon: Building, label: "Industries", color: "indigo", parentCategory: "interests" }
};

// Intake/Personal Info fields (not defaults, collected during intake)
const INTAKE_FIELD_CONFIG = {
  builder_types: { icon: Target, label: "Builder Types", color: "emerald", description: "Collected during intake" }
};

export default function DefaultsInspoAdminPage({ embedded = false, section = null }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("fields");
  const [activeCategory, setActiveCategory] = useState("dietary_options");
  
  // Data
  const [dashboard, setDashboard] = useState(null);
  const [fieldOptions, setFieldOptions] = useState({});
  const [inspoList, setInspoList] = useState([]);
  const [platformConfig, setPlatformConfig] = useState({});
  
  // Edit states
  const [editingOption, setEditingOption] = useState(null);
  const [editForm, setEditForm] = useState({ label: "", description: "", status: "published" });
  const [showAddOption, setShowAddOption] = useState(false);
  const [newOption, setNewOption] = useState({ label: "", description: "", enabled: true, status: "draft" });
  
  // Option image states
  const [optionImages, setOptionImages] = useState({}); // grouped: "cat.field.optId" -> url
  const [uploadingImageFor, setUploadingImageFor] = useState(null); // option id currently uploading
  
  // Inspo states
  const [showAddInspo, setShowAddInspo] = useState(false);
  const [editingInspo, setEditingInspo] = useState(null);
  const [newInspo, setNewInspo] = useState({
    title: "",
    description: "",
    category: "general",
    content_type: "text",
    content: "",
    tags: []
  });
  
  useEffect(() => {
    fetchDashboard();
    fetchFieldOptions();
    fetchInspo();
    fetchConfig();
    fetchOptionImages();
  }, []);
  
  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard:", err);
    }
  };
  
  const fetchFieldOptions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/field-options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFieldOptions(data.field_options || {});
      }
    } catch (err) {
      console.error("Failed to fetch field options:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchInspo = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/inspo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInspoList(data.inspo || []);
      }
    } catch (err) {
      console.error("Failed to fetch inspo:", err);
    }
  };
  
  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlatformConfig(data.config || {});
      }
    } catch (err) {
      console.error("Failed to fetch config:", err);
    }
  };
  
  const fetchOptionImages = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/defaults/option-images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOptionImages(data.grouped || {});
      }
    } catch (err) {
      console.error("Failed to fetch option images:", err);
    }
  };
  
  const handleUploadOptionImage = async (optionId, file) => {
    if (!file) return;
    setUploadingImageFor(optionId);
    try {
      const token = localStorage.getItem("session_token");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", activeCategory);
      formData.append("field_key", activeCategory);
      formData.append("option_id", optionId);
      
      const res = await fetch(`${API}/admin/defaults/option-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success("Image uploaded!");
        // Update local state immediately
        setOptionImages(prev => ({
          ...prev,
          [`${activeCategory}.${activeCategory}.${optionId}`]: data.image_url
        }));
      } else {
        const err = await res.json();
        toast.error(err.detail || "Upload failed");
      }
    } catch (err) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImageFor(null);
    }
  };
  
  const handleDeleteOptionImage = async (optionId) => {
    const imageKey = `${activeCategory}.${activeCategory}.${optionId}`;
    try {
      const token = localStorage.getItem("session_token");
      // Find the image_id from the full images list
      const res = await fetch(`${API}/admin/defaults/option-images?category=${activeCategory}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const img = (data.images || []).find(
          i => i.category === activeCategory && i.option_id === optionId
        );
        if (img) {
          const delRes = await fetch(`${API}/admin/defaults/option-image/${img.image_id}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` }
          });
          if (delRes.ok) {
            toast.success("Image removed");
            setOptionImages(prev => {
              const next = { ...prev };
              delete next[imageKey];
              return next;
            });
          }
        }
      }
    } catch (err) {
      toast.error("Failed to delete image");
    }
  };
  
  const handleAddOption = async () => {
    if (!newOption.label.trim()) {
      toast.error("Label is required");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/field-options/${activeCategory}/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newOption,
          status: newOption.status || "draft"
        })
      });
      
      if (res.ok) {
        toast.success("Option added!");
        setShowAddOption(false);
        setNewOption({ label: "", description: "", enabled: true, status: "draft" });
        fetchFieldOptions();
        fetchDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add option");
      }
    } catch (err) {
      toast.error("Failed to add option");
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteOption = async (optionId) => {
    if (!confirm("Delete this option?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/field-options/${activeCategory}/${optionId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Option deleted");
        fetchFieldOptions();
        fetchDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete option");
      }
    } catch (err) {
      toast.error("Failed to delete option");
    }
  };
  
  const handleToggleOption = async (optionId, currentEnabled) => {
    const currentOptions = fieldOptions[activeCategory] || [];
    const updatedOptions = currentOptions.map(opt =>
      opt.id === optionId ? { ...opt, enabled: !currentEnabled } : opt
    );
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/field-options/${activeCategory}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ options: updatedOptions })
      });
      
      if (res.ok) {
        setFieldOptions(prev => ({
          ...prev,
          [activeCategory]: updatedOptions
        }));
      }
    } catch (err) {
      toast.error("Failed to update option");
    }
  };
  
  const handleResetCategory = async () => {
    if (!confirm(`Reset ${CATEGORY_CONFIG[activeCategory]?.label || activeCategory} to defaults?`)) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/field-options/reset/${activeCategory}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Reset to defaults!");
        fetchFieldOptions();
        fetchDashboard();
      }
    } catch (err) {
      toast.error("Failed to reset");
    }
  };
  
  const handleUpdateOption = async (optionId, updates) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/field-options/${activeCategory}/${optionId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        toast.success("Option updated!");
        setEditingOption(null);
        fetchFieldOptions();
        fetchDashboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update option");
      }
    } catch (err) {
      toast.error("Failed to update option");
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggleStatus = async (optionId, currentStatus) => {
    const newStatus = currentStatus === "published" ? "draft" : "published";
    await handleUpdateOption(optionId, { status: newStatus });
  };
  
  const startEditing = (option) => {
    setEditingOption(option.id);
    setEditForm({
      label: option.label,
      description: option.description || "",
      status: option.status || "published"
    });
  };
  
  const saveEditing = () => {
    if (editingOption) {
      handleUpdateOption(editingOption, editForm);
    }
  };
  
  const handleAddInspo = async () => {
    if (!newInspo.title.trim()) {
      toast.error("Title is required");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/inspo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newInspo)
      });
      
      if (res.ok) {
        toast.success("Inspiration added!");
        setShowAddInspo(false);
        setNewInspo({
          title: "",
          description: "",
          category: "general",
          content_type: "text",
          content: "",
          tags: []
        });
        fetchInspo();
        fetchDashboard();
      }
    } catch (err) {
      toast.error("Failed to add inspiration");
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteInspo = async (inspoId) => {
    if (!confirm("Delete this inspiration?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/inspo/${inspoId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Deleted");
        fetchInspo();
        fetchDashboard();
      }
    } catch (err) {
      toast.error("Failed to delete");
    }
  };
  
  const handleUpdateConfig = async (updates) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/config`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        const data = await res.json();
        setPlatformConfig(data.config);
        toast.success("Config updated");
      }
    } catch (err) {
      toast.error("Failed to update config");
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  const currentCategoryConfig = CATEGORY_CONFIG[activeCategory] || { icon: Settings, label: activeCategory, color: "gray" };
  const CategoryIcon = currentCategoryConfig.icon;
  const currentOptions = fieldOptions[activeCategory] || [];
  
  return (
    <div className={embedded ? "" : "min-h-screen bg-gray-50"}>
      <div className={embedded ? "" : "max-w-7xl mx-auto px-4 py-6"}>
        {/* Header - hidden when embedded */}
        {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-6 h-6 text-violet-600" />
              Platform Defaults & Inspo
            </h1>
            <p className="text-gray-600">Manage global field options and inspiration content</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { fetchFieldOptions(); fetchInspo(); fetchDashboard(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
        )}
        
        {/* Tabs - when section="defaults", only show Field Options; when section="inspo", only show Inspiration; otherwise show all */}
        <Tabs value={section === "defaults" ? "fields" : section === "inspo" ? "inspo" : activeTab} onValueChange={!section ? setActiveTab : undefined}>
          {!section && (
          <TabsList className="mb-6">
            <TabsTrigger value="fields" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Field Options
            </TabsTrigger>
            <TabsTrigger value="inspo" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Inspiration
              {dashboard?.inspo?.total > 0 && (
                <Badge variant="secondary" className="text-xs ml-1">{dashboard.inspo.total}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="config" className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              Platform Config
            </TabsTrigger>
          </TabsList>
          )}
          
          {/* Field Options Tab */}
          <TabsContent value="fields">
            {/* Category Pills */}
            <div className="flex flex-wrap gap-2 mb-5" data-testid="category-pills">
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                const count = dashboard?.field_counts?.[key] || 0;
                const isActive = activeCategory === key;
                return (
                  <button
                    key={key}
                    onClick={() => setActiveCategory(key)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all border ${
                      isActive ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                    }`}
                    data-testid={`category-${key}`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${isActive ? "text-white" : `text-${config.color}-500`}`} />
                    {config.label}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isActive ? "bg-white/20" : "bg-gray-100"}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
            
            <div className="grid lg:grid-cols-1 gap-6">
              
              {/* Options Editor */}
              <div>
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg bg-${currentCategoryConfig.color}-100 flex items-center justify-center`}>
                          <CategoryIcon className={`w-5 h-5 text-${currentCategoryConfig.color}-600`} />
                        </div>
                        <div>
                          <CardTitle>{currentCategoryConfig.label}</CardTitle>
                          <CardDescription>
                            {currentOptions.length} options ({currentOptions.filter(o => o.enabled).length} enabled)
                          </CardDescription>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={handleResetCategory}>
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Reset
                        </Button>
                        <Button size="sm" onClick={() => setShowAddOption(true)}>
                          <Plus className="w-4 h-4 mr-1" />
                          Add Option
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="space-y-2">
                        {currentOptions.map((option) => (
                          <div
                            key={option.id}
                            className={`p-3 border rounded-lg ${
                              option.enabled ? 'bg-white' : 'bg-gray-50 opacity-60'
                            } ${option.status === 'draft' ? 'border-amber-300 border-dashed' : 'border-gray-200'}`}
                          >
                            {editingOption === option.id ? (
                              // Edit mode
                              <div className="space-y-3">
                                <div>
                                  <Label className="text-xs">Label</Label>
                                  <Input
                                    value={editForm.label}
                                    onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                                    className="mt-1"
                                    placeholder="Option label"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Description</Label>
                                  <Textarea
                                    value={editForm.description}
                                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                    className="mt-1"
                                    placeholder="Brief description of this option"
                                    rows={2}
                                  />
                                </div>
                                <div className="flex items-center gap-4">
                                  <div className="flex items-center gap-2">
                                    <Label className="text-xs">Status:</Label>
                                    <Select
                                      value={editForm.status}
                                      onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                                    >
                                      <SelectTrigger className="w-28 h-8">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="draft">Draft</SelectItem>
                                        <SelectItem value="published">Published</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                <div className="flex gap-2 pt-2">
                                  <Button size="sm" onClick={saveEditing} disabled={saving}>
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                                    Save
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => setEditingOption(null)}>
                                    <X className="w-4 h-4 mr-1" />
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              // View mode
                              <div className="flex items-start gap-3">
                                <GripVertical className="w-4 h-4 text-gray-400 cursor-grab mt-1" />
                                
                                {/* Option Image Thumbnail */}
                                {(() => {
                                  const imgKey = `${activeCategory}.${activeCategory}.${option.id}`;
                                  const imgUrl = optionImages[imgKey];
                                  return (
                                    <div className="relative w-10 h-10 rounded-lg border border-gray-200 overflow-hidden flex-shrink-0 group">
                                      {imgUrl ? (
                                        <>
                                          <img 
                                            src={imgUrl.startsWith('http') ? imgUrl : `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`} 
                                            alt={option.label} 
                                            className="w-full h-full object-cover"
                                          />
                                          <button
                                            className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                            onClick={() => handleDeleteOptionImage(option.id)}
                                            title="Remove image"
                                            data-testid={`option-img-delete-${option.id}`}
                                          >
                                            <X className="w-3.5 h-3.5 text-white" />
                                          </button>
                                        </>
                                      ) : (
                                        <label 
                                          className="w-full h-full flex items-center justify-center bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                                          title="Upload image"
                                          data-testid={`option-img-upload-${option.id}`}
                                        >
                                          {uploadingImageFor === option.id ? (
                                            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                                          ) : (
                                            <ImagePlus className="w-4 h-4 text-gray-400" />
                                          )}
                                          <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => {
                                              const file = e.target.files?.[0];
                                              if (file) handleUploadOptionImage(option.id, file);
                                              e.target.value = '';
                                            }}
                                          />
                                        </label>
                                      )}
                                    </div>
                                  );
                                })()}
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-sm">{option.label}</p>
                                    {option.status === 'draft' && (
                                      <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">
                                        Draft
                                      </Badge>
                                    )}
                                  </div>
                                  {option.description && (
                                    <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                                  )}
                                  <p className="text-[10px] text-gray-400 mt-1">ID: {option.id}</p>
                                </div>
                                
                                <div className="flex items-center gap-1">
                                  {/* Upload image button (when image exists, allow replacing) */}
                                  {optionImages[`${activeCategory}.${activeCategory}.${option.id}`] && (
                                    <label
                                      className="h-8 w-8 p-0 inline-flex items-center justify-center rounded-md text-sm font-medium hover:bg-gray-100 cursor-pointer"
                                      title="Replace image"
                                      data-testid={`option-img-replace-${option.id}`}
                                    >
                                      {uploadingImageFor === option.id ? (
                                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                                      ) : (
                                        <Upload className="w-4 h-4 text-gray-500" />
                                      )}
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                          const file = e.target.files?.[0];
                                          if (file) handleUploadOptionImage(option.id, file);
                                          e.target.value = '';
                                        }}
                                      />
                                    </label>
                                  )}
                                  
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => startEditing(option)}
                                    title="Edit"
                                  >
                                    <Edit2 className="w-4 h-4 text-gray-500" />
                                  </Button>
                                  
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`h-8 px-2 text-xs ${option.status === 'published' ? 'text-green-600' : 'text-amber-600'}`}
                                    onClick={() => handleToggleStatus(option.id, option.status || 'published')}
                                    title={option.status === 'published' ? 'Unpublish' : 'Publish'}
                                  >
                                    {option.status === 'published' ? 'Live' : 'Publish'}
                                  </Button>
                                  
                                  <Switch
                                    checked={option.enabled}
                                    onCheckedChange={() => handleToggleOption(option.id, option.enabled)}
                                  />
                                  
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => handleDeleteOption(option.id)}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {currentOptions.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <Settings className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-sm">No options yet</p>
                            <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowAddOption(true)}>
                              <Plus className="w-4 h-4 mr-1" />
                              Add First Option
                            </Button>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
          
          {/* Inspiration Tab */}
          <TabsContent value="inspo">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setShowAddInspo(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Inspiration
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {inspoList.length > 0 ? (
                inspoList.map((inspo) => (
                  <Card key={inspo.inspo_id} className="overflow-hidden">
                    {inspo.image_url && (
                      <div className="h-32 bg-gray-100">
                        <img src={inspo.image_url} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium">{inspo.title}</h4>
                        {inspo.is_featured && <Star className="w-4 h-4 text-amber-500 fill-amber-500" />}
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{inspo.description}</p>
                      <div className="flex items-center gap-2 mt-3">
                        <Badge variant="outline" className="text-xs">{inspo.category}</Badge>
                        <Badge variant="outline" className="text-xs">{inspo.content_type}</Badge>
                      </div>
                      <div className="flex items-center justify-end gap-1 mt-3">
                        <Button variant="ghost" size="sm" onClick={() => setEditingInspo(inspo)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteInspo(inspo.inspo_id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center">
                    <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-500">No inspiration content yet</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowAddInspo(true)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add First
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
          
          {/* Platform Config Tab */}
          <TabsContent value="config">
            <Card>
              <CardHeader>
                <CardTitle>Category Configuration</CardTitle>
                <CardDescription>Per-category settings controlling data collection and feature toggles</CardDescription>
              </CardHeader>
              <CardContent>
                <PerCategoryConfig platformConfig={platformConfig} onUpdate={handleUpdateConfig} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Add Option Dialog */}
      <Dialog open={showAddOption} onOpenChange={setShowAddOption}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add {currentCategoryConfig.label.slice(0, -1)}</DialogTitle>
            <DialogDescription>Add a new option to {currentCategoryConfig.label}. New options start as drafts.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Label *</Label>
              <Input
                value={newOption.label}
                onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                placeholder="e.g., Pescatarian"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={newOption.description || ""}
                onChange={(e) => setNewOption({ ...newOption, description: e.target.value })}
                placeholder="Brief description of this option"
                rows={2}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={newOption.enabled}
                  onCheckedChange={(checked) => setNewOption({ ...newOption, enabled: checked })}
                />
                <Label className="text-sm">Enabled</Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-sm">Status:</Label>
                <Select
                  value={newOption.status || "draft"}
                  onValueChange={(v) => setNewOption({ ...newOption, status: v })}
                >
                  <SelectTrigger className="w-28 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <strong>Note:</strong> Draft options are hidden from guests and clients. Publish when ready.
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddOption(false)}>Cancel</Button>
            <Button onClick={handleAddOption} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Inspiration Dialog */}
      <Dialog open={showAddInspo} onOpenChange={setShowAddInspo}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Inspiration</DialogTitle>
            <DialogDescription>Add example content or templates</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={newInspo.title}
                onChange={(e) => setNewInspo({ ...newInspo, title: e.target.value })}
                placeholder="Inspiration title"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={newInspo.description}
                onChange={(e) => setNewInspo({ ...newInspo, description: e.target.value })}
                placeholder="Brief description"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select
                  value={newInspo.category}
                  onValueChange={(v) => setNewInspo({ ...newInspo, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="profile">Profile</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Type</Label>
                <Select
                  value={newInspo.content_type}
                  onValueChange={(v) => setNewInspo({ ...newInspo, content_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="template">Template</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Content</Label>
              <Textarea
                value={newInspo.content}
                onChange={(e) => setNewInspo({ ...newInspo, content: e.target.value })}
                placeholder="The actual inspiration content..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddInspo(false)}>Cancel</Button>
            <Button onClick={handleAddInspo} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
